import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClub } from '../App';
import api from '../utils/api';
import { Trophy, Medal, TrendingUp, Users, Target } from 'lucide-react';
import { RatingBadge } from '../components/RatingBadge';

function RankBadge({ rank }) {
  if (rank === 1) return <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-400 text-white text-[9px] font-bold">1</span>;
  if (rank === 2) return <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-400 text-white text-[9px] font-bold">2</span>;
  if (rank === 3) return <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-700 text-white text-[9px] font-bold">3</span>;
  return <span className="text-[10px] text-slate-500 font-medium">{rank}</span>;
}

export default function StandingsPage() {
  const { user } = useAuth();
  const { selectedClub } = useClub();
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingType, setRatingType] = useState('doubles');

  const canManage = selectedClub?.my_role === 'admin' || selectedClub?.my_role === 'contributor' || user?.role === 'admin';

  useEffect(() => {
    if (!selectedClub?.id) return;
    api.get(`/standings/lifetime?group_id=${selectedClub.id}`)
      .then(res => setStandings(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedClub?.id]);

  const sortedStandings = [...standings].sort((a, b) => {
    const rA = ratingType === 'singles'
      ? (a.club_singles_rating || a.group_singles_rating || a.lifetime_singles_rating || 1500)
      : (a.club_doubles_rating || a.group_doubles_rating || a.lifetime_doubles_rating || 1500);
    const rB = ratingType === 'singles'
      ? (b.club_singles_rating || b.group_singles_rating || b.lifetime_singles_rating || 1500)
      : (b.club_doubles_rating || b.group_doubles_rating || b.lifetime_doubles_rating || 1500);
    return rB - rA;
  }).map((p, i) => ({ ...p, rank: i + 1 }));

  const getClubRating = (p) => ratingType === 'singles'
    ? (p.club_singles_rating || p.group_singles_rating)
    : (p.club_doubles_rating || p.group_doubles_rating);
  const getLifetimeRating = (p) => ratingType === 'singles' ? p.lifetime_singles_rating : p.lifetime_doubles_rating;
  const getGames = (p) => ratingType === 'singles' ? p.singles_games : p.doubles_games;

  return (
    <div className="max-w-2xl mx-auto space-y-3">
      {/* Header with inline toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Standings</h1>
          <p className="text-[11px] text-slate-500">{selectedClub?.name}</p>
        </div>
        <div className="flex gap-0.5 bg-slate-100 p-0.5 rounded-lg">
          <button
            onClick={() => setRatingType('doubles')}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold transition-all ${
              ratingType === 'doubles' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-doubles"
          >
            <Users size={10} /> Doubles
          </button>
          <button
            onClick={() => setRatingType('singles')}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold transition-all ${
              ratingType === 'singles' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-singles"
          >
            <Target size={10} /> Singles
          </button>
        </div>
      </div>

      {/* Full table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-testid="lifetime-standings-table">
        <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-1.5">
          <TrendingUp size={13} className="text-green-600" />
          <span className="text-xs font-bold text-slate-900">
            All Players · {ratingType === 'singles' ? 'Singles' : 'Doubles'}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="px-2 py-1.5 text-left text-[9px] font-semibold text-slate-500 uppercase w-8">#</th>
                <th className="px-1 py-1.5 text-left text-[9px] font-semibold text-slate-500 uppercase">Player</th>
                <th className="px-1 py-1.5 text-right text-[9px] font-semibold text-slate-500 uppercase">Club</th>
                <th className="px-1 py-1.5 text-right text-[9px] font-semibold text-slate-500 uppercase">Life</th>
                <th className="px-1 py-1.5 text-right text-[9px] font-semibold text-slate-500 uppercase">G</th>
                <th className="px-1 py-1.5 text-right text-[9px] font-semibold text-slate-500 uppercase">W/L</th>
                <th className="px-1 py-1.5 text-right text-[9px] font-semibold text-slate-500 uppercase">W%</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j} className="px-2 py-2"><div className="h-3 bg-slate-100 rounded animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : sortedStandings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                    <Medal className="w-7 h-7 mx-auto mb-2 text-slate-300" />
                    <p className="text-xs">No players yet</p>
                  </td>
                </tr>
              ) : (
                sortedStandings.map((p, i) => {
                  const isCurrentPlayer = user?.player_id === p.player_id;
                  return (
                    <tr
                      key={p.player_id}
                      className={`transition-colors ${i < sortedStandings.length - 1 ? 'border-b border-slate-100' : ''} ${isCurrentPlayer ? 'bg-green-50' : 'hover:bg-slate-50'}`}
                      data-testid={`standing-row-${p.player_id}`}
                    >
                      <td className="px-2 py-1.5 w-8">
                        <RankBadge rank={p.rank} />
                      </td>
                      <td className="px-1 py-1.5 max-w-[90px]">
                        {(canManage || isCurrentPlayer) ? (
                          <Link to={`/players/${p.player_id}`} className="flex items-center gap-1 group">
                            <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-semibold text-[9px] flex-shrink-0">
                              {p.player_name[0]?.toUpperCase()}
                            </div>
                            <span className={`text-[10px] font-medium group-hover:text-green-700 transition-colors truncate ${isCurrentPlayer ? 'text-green-700' : 'text-slate-900'}`}>
                              {p.player_name}{isCurrentPlayer && <span className="text-[9px] font-normal text-green-500"> (you)</span>}
                            </span>
                          </Link>
                        ) : (
                          <div className="flex items-center gap-1">
                            <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-semibold text-[9px] flex-shrink-0">
                              {p.player_name[0]?.toUpperCase()}
                            </div>
                            <span className="text-[10px] font-medium text-slate-900 truncate">{p.player_name}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-1 py-1.5 text-right">
                        <span className="font-mono font-bold text-slate-900 text-[10px]">{getClubRating(p) || 1500}</span>
                        <div className="mt-0.5 flex justify-end"><RatingBadge status={p.rating_status} /></div>
                      </td>
                      <td className="px-1 py-1.5 text-right">
                        <span className="font-mono text-slate-500 text-[10px]">{getLifetimeRating(p) || 1500}</span>
                      </td>
                      <td className="px-1 py-1.5 text-right text-[10px] text-slate-600">{getGames(p) || 0}</td>
                      <td className="px-1 py-1.5 text-right text-[10px]">
                        <span className="text-green-600 font-medium">{p.total_wins}</span>
                        <span className="text-slate-300 mx-0.5">/</span>
                        <span className="text-red-500 font-medium">{p.total_losses}</span>
                      </td>
                      <td className="px-1 py-1.5 text-right">
                        <span className={`text-[10px] font-semibold ${p.win_rate >= 50 ? 'text-green-600' : 'text-slate-500'}`}>
                          {p.win_rate}%
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
